import { AxiosResponse } from "axios";
import { useApiRequest } from "../helper/api";
import {
  createProjectUserPayloadModel,
  deleteuserInProject,
  exportTeamOptionResponse,
  ProjectDetailResponse,
  ProjectResponse,
  updateProjectActivityPayload,
} from "../model/projectModel";
import { toast } from "react-toastify";

const useProjectApi = () => {
  const { fetchData, waitForAllRequests } = useApiRequest();

  const fetchProject = async (userID: number, status: number): Promise<ProjectResponse[] | null> => {
    const response = await fetchData("/manage/get_projects", { method: "POST", data: { user_id: userID, status } });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const fetchProjectDetail = async (projectID: number): Promise<ProjectDetailResponse | null> => {
    const response = await fetchData("/manage/get_project_details", {
      method: "POST",
      data: { project_id: projectID },
    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const fetchTeamOptionDetail = async (projectID: number): Promise<exportTeamOptionResponse | null> => {
    const response = await fetchData("/manage/get_add_team_member_details", {
      method: "POST",
      data: { project_id: projectID },
    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const fetchGetPromiseProjectDetail = async (
    projectID: number,
  ): Promise<{
    projectDetails: ProjectDetailResponse | null;
    OptionDetail: exportTeamOptionResponse | null;
  } | null> => {
    const [responseProjectDetail, response] = await Promise.all([
      fetchData("/manage/get_project_details", {
        method: "POST",
        data: { project_id: projectID },
      }),
      fetchData("/manage/get_add_team_member_details", {
        method: "POST",
        data: { project_id: projectID },
      }),
    ]);

    await waitForAllRequests();
    if (responseProjectDetail?.status && response?.status) {
      return { projectDetails: responseProjectDetail.data, OptionDetail: response.data };
    }
    return null;
  };

  const createProjectUser = async (
    projectUser: createProjectUserPayloadModel,
  ): Promise<AxiosResponse | null> => {
    const response = await fetchData("/manage/add_edit_project_user", {
      method: "POST",
      data: projectUser,
    });
    if (response?.status) {
      toast.success(response.data);
    }
    return response?.data;
  };

  const updateProjectActivityApi = async (
    activityPayload: updateProjectActivityPayload[],
  ): Promise<AxiosResponse | null> => {
    const response = await fetchData("/manage/update-project-activities/", {
      method: "POST",
      data: activityPayload,
    });
    if (response?.status) {
      toast.success(response.data);
    }
    return response?.data;
  };

  const addProjectActivityApi = async (
    activityPayload: updateProjectActivityPayload[],
  ): Promise<AxiosResponse | null> => {
    const response = await fetchData("/manage/add-project-activity/", {
      method: "POST",
      data: activityPayload,
    });
    if (response?.status) {
      toast.success(response.data);
    }
    return response?.data;
  };

  const deleteProjectActivityApi = async (payload: {
    projects_id: number;
    version_id: number;
  }): Promise<AxiosResponse | null> => {
    const response = await fetchData("/manage/delete-project-activity/", {
      method: "POST",
      data: { ...payload, is_enabled: false, activity_name: "" },
    });
    if (response?.status) {
      toast.success("Activity removed");
    }
    return response?.data;
  };

  const toggleMandatoryActivityApi = async (payload: {
    version_id: number;
    projects_id: number;
  }): Promise<{ is_mandatory: boolean } | null> => {
    const response = await fetchData("/manage/toggle-mandatory-activity/", {
      method: "POST",
      data: { ...payload, is_enabled: false, activity_name: "" },
    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const editProjectActivityApi = async (payload: {
    projects_id: number;
    version_id: number;
    activity_name: string;
  }): Promise<AxiosResponse | null> => {
    const response = await fetchData("/manage/edit-project-activity/", {
      method: "POST",
      data: { ...payload, is_enabled: true },
    });
    if (response?.status) {
      toast.success(response.data || "Activity name updated");
    }
    return response?.data;
  };

  const fetchDeleteUserInProject = async (payload: deleteuserInProject): Promise<AxiosResponse | null> => {
    const response = await fetchData(`/manage/delete-user/`, {
      method: "POST",
      data: payload,
    });
    if (response?.status) {
      // console.log("responsd", response);
      toast.success(response.data);
      return response.data;
    }
    return null;
  };

  return {
    fetchProject,
    fetchProjectDetail,
    fetchTeamOptionDetail,
    createProjectUser,
    updateProjectActivityApi,
    addProjectActivityApi,
    editProjectActivityApi,
    deleteProjectActivityApi,
    toggleMandatoryActivityApi,
    fetchGetPromiseProjectDetail,
    fetchDeleteUserInProject
  };
};

export default useProjectApi;
